import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { Button, Modal, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import DataTable from "../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../Common/DataTableFooter/DataTableFooter";

import { setCurrentPage } from "../../../../Redux/slice/features/paginationSilce";
import { propertyColumns } from "./PropertiesData";
import { REACT_APP_API_URL } from "../../../../env";

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ================= VIEW MODAL STATE =================
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // ================= EDIT MODAL STATE =================
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const dispatch = useDispatch();

  const currentPage = useSelector((state) => state.pagination.currentPage);
  const itemsPerPage = useSelector((state) => state.pagination.itemsPerPage);

  // ================= FETCH PROPERTY LIST =================
  const fetchProperties = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${REACT_APP_API_URL}api/dashboard/admin/property/list`,
        {
          params: {
            P_CHRACTION: "V",
            P_PAGENO: currentPage,
            P_PAGESIZE: itemsPerPage,
          },
        }
      );
      if (res.data.success) {
        setProperties(res.data.data || []);
        setTotalRecords(res.data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch properties:", err);
      setError("Error fetching property list");
    } finally {
      setLoading(false);
    }
  };

  // ================= RESET PAGE =================
  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  // ================= FETCH DATA =================
  useEffect(() => {
    fetchProperties();
  }, [currentPage, itemsPerPage]);

  // ================= VIEW PROPERTY =================
  const handleView = async (id) => {
    try {
      setViewLoading(true);
      setShowViewModal(true);
      setViewData(null);
      const res = await axios.get(
        `${REACT_APP_API_URL}api/dashboard/admin/property/edit/${id}`
      );
      if (res.data.success) {
        setViewData(res.data.data);
      }
    } catch (error) {
      console.error("View Error:", error);
      alert("Failed to load property details");
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  // ================= EDIT PROPERTY =================
  const handleEdit = async (id) => {
    try {
      setEditLoading(true);
      setShowEditModal(true);
      setEditData(null);
      const res = await axios.get(
        `${REACT_APP_API_URL}api/dashboard/admin/property/edit/${id}`
      );
      if (res.data.success) {
        setEditData(res.data.data);
      }
    } catch (error) {
      console.error("Edit Error:", error);
      alert("Failed to load property for editing");
      setShowEditModal(false);
    } finally {
      setEditLoading(false);
    }
  };

  // ================= SAVE EDIT =================
  // ⬇️ Ab PUT /property/update/:id call hoga — action 'U' + property ID
  const handleSaveEdit = async () => {
    if (!editData || !editData.PropertyID) {
      alert("Property ID missing!");
      return;
    }

    try {
      setSaveLoading(true);

      const res = await axios.put(
        `${REACT_APP_API_URL}api/dashboard/admin/property/update/${editData.PropertyID}`,
        {
          vchUnitName: editData.UnitName,
          intPropertyTypeID: editData.PropertyTypeID,
          vchContactPerson: editData.ContactPerson,
          vchMobileNo: editData.MobileNo,
          vchLandLineNo: editData.LandLineNo || '',
          vchFAXNo: editData.FAXNo || '',
          vchEmailID: editData.EmailID,
          vchAddress: editData.Address,
          intStateID: editData.StateID,
          intDistrictID: editData.DistrictID,
          intLocationID: editData.LocationID,
          vchPIN: editData.PIN,
          vchPropertyInfoDetails: editData.PropertyDetails,
          vchSnippet: editData.Snippet,
          vchLatitude: editData.Latitude,
          vchLongitude: editData.Longitude,
          vchChkInTime: editData.CheckedInTime || '',
          vchChkOutTime: editData.CheckedOutTime || '',
          intShowinWeb: editData.ShowinWeb,
          intBookingAvailable: editData.BookingAvailable,
          vchPropertyAbbreviation: editData.Abbreviation || '',
          intCreatedBy: 1,
        }
      );

      if (res.data.success) {
        alert("Property updated successfully");
        setShowEditModal(false);
        fetchProperties(); // List refresh hogi
      } else {
        alert(res.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Save Error:", error);
      alert(error.response?.data?.message || "Failed to save changes");
    } finally {
      setSaveLoading(false);
    }
  };

  // ================= DELETE PROPERTY =================
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this property?"
    );
    if (!confirmDelete) return;

    try {
      const res = await axios.delete(
        `${REACT_APP_API_URL}api/dashboard/admin/property/delete/${id}`
      );
      if (res.data.success) {
        alert("Property deleted successfully");
        fetchProperties();
      } else {
        alert(res.data.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      if (error.response) {
        alert(error.response.data.message);
      } else {
        alert("Delete failed");
      }
    }
  };

  // ================= TABLE DATA =================
  const formattedData = properties.map((item) => ({
    propertyId: item.PropertyID,
    unitName: item.UnitName,
    propertyType: item.PropertyTypeName,
    contactPerson: item.ContactPerson,
    mobileNo: item.MobileNo,
    landlineNo: item.LandLineNo,
    faxNo: item.FAXNo,
    emailId: item.EmailID,
    address: item.Address,

    actions: (
      <>
        {/* VIEW BUTTON */}
        <Button
          size="sm"
          variant="outline-primary"
          className="me-1"
          onClick={() => handleView(item.PropertyID)}
        >
          <FaEye />
        </Button>

        {/* EDIT BUTTON */}
        <Button
          size="sm"
          variant="outline-success"
          className="me-1"
          onClick={() => handleEdit(item.PropertyID)}
        >
          <FaEdit />
        </Button>

        {/* DELETE BUTTON */}
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => handleDelete(item.PropertyID)}
        >
          <FaTrash />
        </Button>
      </>
    ),
  }));

  // ================= PAGINATION =================
  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalRecords / itemsPerPage)) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-body">

        <h3>Property List</h3>

        {loading && <h4>Loading...</h4>}
        {error && <p className="text-danger">Error: {error}</p>}

        <DataTableHeader columns={propertyColumns} data={formattedData} />
        <DataTable
          columns={propertyColumns}
          data={formattedData}
          enablePagination={false}
        />
        <DataTableFooter
          dataT={formattedData}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
        />

      </div>

      {/* ================= VIEW MODAL ================= */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Property Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading ? (
            <p>Loading...</p>
          ) : viewData ? (
            <table className="table table-bordered table-striped">
              <tbody>
                <tr><th style={{ width: "35%" }}>Unit Name</th><td>{viewData.UnitName || "-"}</td></tr>
                <tr><th>Contact Person</th><td>{viewData.ContactPerson || "-"}</td></tr>
                <tr><th>Mobile No</th><td>{viewData.MobileNo || "-"}</td></tr>
                <tr><th>Email ID</th><td>{viewData.EmailID || "-"}</td></tr>
                <tr><th>Address</th><td>{viewData.Address || "-"}</td></tr>
                <tr><th>PIN</th><td>{viewData.PIN || "-"}</td></tr>
                <tr><th>District</th><td>{viewData.District || "-"}</td></tr>
                <tr><th>Location</th><td>{viewData.LocationName || "-"}</td></tr>
                <tr><th>Latitude</th><td>{viewData.Latitude || "-"}</td></tr>
                <tr><th>Longitude</th><td>{viewData.Longitude || "-"}</td></tr>
                <tr><th>Abbreviation</th><td>{viewData.Abbreviation || "-"}</td></tr>
                <tr><th>Show in Web</th><td>{viewData.ShowinWeb === 0 ? "Yes" : "No"}</td></tr>
                <tr><th>Booking Available</th><td>{viewData.BookingAvailable === 1 ? "Yes" : "No"}</td></tr>
                <tr><th>Booking URL</th><td>{viewData.URL || "-"}</td></tr>
                <tr><th>Snippet</th><td>{viewData.Snippet || "-"}</td></tr>
                <tr><th>Property Details</th><td>{viewData.PropertyDetails || "-"}</td></tr>
              </tbody>
            </table>
          ) : (
            <p>No data found.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= EDIT MODAL ================= */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Property</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLoading ? (
            <p>Loading...</p>
          ) : editData ? (
            <Form>
              <div className="row">

                <div className="col-md-6 mb-3">
                  <Form.Label>Unit Name</Form.Label>
                  <Form.Control
                    value={editData.UnitName || ""}
                    onChange={(e) => setEditData({ ...editData, UnitName: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Contact Person</Form.Label>
                  <Form.Control
                    value={editData.ContactPerson || ""}
                    onChange={(e) => setEditData({ ...editData, ContactPerson: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Mobile No</Form.Label>
                  <Form.Control
                    value={editData.MobileNo || ""}
                    onChange={(e) => setEditData({ ...editData, MobileNo: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Email ID</Form.Label>
                  <Form.Control
                    value={editData.EmailID || ""}
                    onChange={(e) => setEditData({ ...editData, EmailID: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>PIN</Form.Label>
                  <Form.Control
                    value={editData.PIN || ""}
                    onChange={(e) => setEditData({ ...editData, PIN: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Abbreviation</Form.Label>
                  <Form.Control
                    value={editData.Abbreviation || ""}
                    onChange={(e) => setEditData({ ...editData, Abbreviation: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Latitude</Form.Label>
                  <Form.Control
                    value={editData.Latitude || ""}
                    onChange={(e) => setEditData({ ...editData, Latitude: e.target.value })}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Longitude</Form.Label>
                  <Form.Control
                    value={editData.Longitude || ""}
                    onChange={(e) => setEditData({ ...editData, Longitude: e.target.value })}
                  />
                </div>


                <div className="col-md-12 mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editData.Address || ""}
                    onChange={(e) => setEditData({ ...editData, Address: e.target.value })}
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <Form.Label>Snippet</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editData.Snippet || ""}
                    onChange={(e) => setEditData({ ...editData, Snippet: e.target.value })}
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <Form.Label>Property Details</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editData.PropertyDetails || ""}
                    onChange={(e) => setEditData({ ...editData, PropertyDetails: e.target.value })}
                  />
                </div>

              </div>
            </Form>
          ) : (
            <p>No data found.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleSaveEdit} disabled={saveLoading}>
            {saveLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default PropertyList;